// 前端與 Pages Functions 共用的型別定義。

export type VolunteerRole = "english_seed" | "activity";
export type RoleBucket = "seed" | "activity";
export type EmailType = "school" | "personal";
export type VolunteerStatus = "draft" | "submitted";
export type EventCategory = "morning_reading" | "charity";
export type LocationKey = "daoxiang" | "jian" | "charity" | "other";

export const ROLE_LABELS: Record<VolunteerRole, string> = {
  english_seed: "英文種子教師",
  activity: "活動志工",
};

export const GRADE_OPTIONS = [
  "大一",
  "大二",
  "大三",
  "大四",
  "碩士一",
  "碩士二",
  "其他",
] as const;
export type Grade = (typeof GRADE_OPTIONS)[number];

/** 一位志工在名額計算上歸屬哪個 bucket：有種子教師身分就算種子，否則算志工。 */
export function bucketOf(roles: VolunteerRole[]): RoleBucket {
  return roles.includes("english_seed") ? "seed" : "activity";
}

/** 前台可見的場次資料（不含任何個資）。 */
export interface PublicEvent {
  id: number;
  category: EventCategory;
  title: string;
  location: string | null;
  locationKey: LocationKey;
  eventDate: string | null; // YYYY-MM-DD
  dateConfirmed: boolean;
  startTime: string | null;
  endTime: string | null;
  creditHours: number;
  format: string;
  eligibleRoles: VolunteerRole[];
  capacitySeed: number | null; // 英文種子教師名額；null = 不限
  capacityActivity: number | null; // 活動志工名額；null = 不限
  confirmedSeed: number; // 種子教師已正取人數
  confirmedActivity: number; // 志工已正取人數
  waitlistSeed: number; // 種子教師已遞補人數
  waitlistActivity: number; // 志工已遞補人數
  selectedCount: number; // 已勾選總人數
  note: string | null;
  isOpen: boolean;
  monthGroup: string; // YYYY-MM
  sortOrder: number;
}

export interface FormConfig {
  formOpen: boolean;
  deadline: string | null; // YYYY-MM-DD
  minHours: number;
  deadlinePassed: boolean;
  serverDate: string; // YYYY-MM-DD（伺服器判定用）
}

export interface VolunteerProfileInput {
  name: string;
  department: string;
  grade: string;
  phone: string;
  note?: string;
  /** 前台的單一「聯絡 Email」欄位。 */
  email?: string;
  /** 後台編輯相容用；一般前台不再送這兩個。 */
  schoolEmail?: string;
  personalEmail?: string;
  roles: VolunteerRole[];
}

export interface SaveRequest extends VolunteerProfileInput {
  eventIds: number[];
  action: "draft" | "submit";
}

/** 回給前台的志工資料（本人可見，含自己的 Email）。 */
export interface VolunteerRecord {
  id: number;
  name: string;
  department: string;
  grade: string;
  phone: string;
  note: string | null;
  schoolEmail: string | null;
  personalEmail: string | null;
  primaryEmail: string;
  roles: VolunteerRole[];
  status: VolunteerStatus;
  totalHours: number;
  eventIds: number[];
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
}

export interface SummaryEvent {
  id: number;
  title: string;
  location: string | null;
  eventDate: string | null;
  startTime: string | null;
  endTime: string | null;
  creditHours: number;
  category: EventCategory;
  bucket: RoleBucket;
  waitlistPosition: number; // 0 = 正取；>=1 = 遞補序位
}

export interface SaveResponse {
  ok: true;
  volunteer: VolunteerRecord;
  summary: {
    name: string;
    totalHours: number;
    confirmedHours: number;
    waitlistHours: number;
    minHours: number;
    morningCount: number;
    charityCount: number;
    waitlistCount: number;
    events: SummaryEvent[];
  };
}

export interface ApiError {
  ok: false;
  error: string;
  fields?: Record<string, string>;
}

// ---------------- 管理後台 ----------------

export interface AdminVolunteerRow extends VolunteerRecord {
  events: Array<{
    id: number;
    title: string;
    location: string | null;
    eventDate: string | null;
    creditHours: number;
    category: EventCategory;
    bucket: RoleBucket;
    waitlistPosition: number;
  }>;
}

export interface AdminEvent extends Omit<PublicEvent, never> {
  createdAt: string;
  updatedAt: string;
}

export interface AdminEventInput {
  category: EventCategory;
  title: string;
  location: string | null;
  locationKey: LocationKey;
  eventDate: string | null;
  dateConfirmed: boolean;
  startTime: string | null;
  endTime: string | null;
  creditHours: number;
  format: string;
  eligibleRoles: VolunteerRole[];
  capacitySeed: number | null;
  capacityActivity: number | null;
  note: string | null;
  isOpen: boolean;
  monthGroup: string;
  sortOrder: number;
}

export interface AdminSettings {
  formOpen: boolean;
  deadline: string | null;
  minHours: number;
}
